import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquare, PhoneCall, Calendar, HelpCircle, X, Send, CheckCircle2 } from "lucide-react";

const ADVISER = {
  name: "Sarah Jenkins",
  firm: "Migration Portal",
  title: "Senior Migration Adviser",
  avatar:
    "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?crop=entropy&cs=srgb&fm=jpg&w=200&q=80",
  status: "Online • Response < 15m",
};

const ACTIONS = [
  { id: "message", label: "Message", icon: MessageSquare, testid: "adviser-btn-message" },
  { id: "call", label: "Call", icon: PhoneCall, testid: "adviser-btn-call" },
  { id: "appointment", label: "Appointment", icon: Calendar, testid: "adviser-btn-appointment" },
  { id: "ask", label: "Ask", icon: HelpCircle, testid: "adviser-btn-ask" },
];

const SLOTS = ["Today · 4:00 PM", "Tomorrow · 10:30 AM", "Tomorrow · 2:00 PM", "Fri · 11:00 AM"];

export function AdviserFab({ onOpen }) {
  return (
    <button
      data-testid="adviser-fab"
      onClick={onOpen}
      className="fixed bottom-[76px] right-4 z-30 flex items-center gap-2 rounded-full border border-amber-300/40 py-1.5 pl-1.5 pr-3.5 backdrop-blur-md transition-transform active:scale-95"
      style={{ background: "rgba(10,14,23,0.9)" }}
    >
      <img src={ADVISER.avatar} alt="adviser" className="h-9 w-9 rounded-full object-cover" />
      <span className="text-left">
        <span className="block text-[11px] font-semibold leading-tight text-white">Your Adviser</span>
        <span className="block text-[10px] leading-tight text-emerald-300">● Online</span>
      </span>
    </button>
  );
}

export function AdviserConcierge({ open, onClose, initialTab = "message" }) {
  const [tab, setTab] = useState(initialTab);
  const [msgs, setMsgs] = useState([
    { from: "adviser", text: "Hello, I'm Sarah — your single point of contact. How can I help with your journey today?" },
  ]);
  const [input, setInput] = useState("");
  const [booked, setBooked] = useState(null);

  const send = () => {
    if (!input.trim()) return;
    const text = input.trim();
    setMsgs((m) => [...m, { from: "me", text }]);
    setInput("");
    setTimeout(() => {
      setMsgs((m) => [
        ...m,
        { from: "adviser", text: "Thank you — I've noted that. I'll coordinate the right specialists and update your journey shortly." },
      ]);
    }, 700);
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            data-testid="adviser-concierge-modal"
            className="fixed inset-x-0 bottom-0 z-[61] mx-auto flex h-[82vh] max-w-lg flex-col rounded-t-3xl border-t border-white/10"
            style={{ background: "rgba(10,14,23,0.99)" }}
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
          >
            <div className="flex items-center gap-3 border-b border-white/10 p-4">
              <img src={ADVISER.avatar} alt="adviser" className="h-12 w-12 rounded-full object-cover ring-2 ring-amber-300/30" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-white">{ADVISER.name}</p>
                <p className="text-[11px] text-slate-300">{ADVISER.firm}</p>
                <p className="text-[11px] text-emerald-300">{ADVISER.status}</p>
              </div>
              <button onClick={onClose} data-testid="adviser-close" className="grid h-9 w-9 place-items-center rounded-full border border-white/10 text-slate-300">
                <X size={16} />
              </button>
            </div>

            <div className="flex gap-2 px-4 pt-3">
              {ACTIONS.map((a) => {
                const Icon = a.icon;
                const active = tab === a.id;
                return (
                  <button
                    key={a.id}
                    data-testid={a.testid}
                    onClick={() => setTab(a.id)}
                    className="flex flex-1 flex-col items-center gap-1 rounded-xl border py-2 text-[11px] transition-colors"
                    style={{
                      borderColor: active ? "rgba(226,194,117,0.5)" : "rgba(255,255,255,0.08)",
                      color: active ? "#E2C275" : "#94A3B8",
                      background: active ? "rgba(226,194,117,0.08)" : "transparent",
                    }}
                  >
                    <Icon size={16} />
                    {a.label}
                  </button>
                );
              })}
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {(tab === "message" || tab === "ask") && (
                <div className="flex flex-col gap-2.5">
                  {msgs.map((m, i) => (
                    <div
                      key={i}
                      className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm ${
                        m.from === "me"
                          ? "self-end bg-amber-300 text-[#07090E]"
                          : "self-start border border-white/10 bg-white/5 text-slate-200"
                      }`}
                    >
                      {m.text}
                    </div>
                  ))}
                </div>
              )}

              {tab === "call" && (
                <div className="flex flex-col items-center justify-center py-10 text-center">
                  <div className="grid h-20 w-20 place-items-center rounded-full bg-emerald-400/15 text-emerald-300">
                    <PhoneCall size={30} />
                  </div>
                  <p className="mt-4 text-sm text-slate-300">Request an instant call back from {ADVISER.name}.</p>
                  <button className="mt-4 rounded-full bg-emerald-400 px-6 py-2.5 text-sm font-semibold text-[#07090E]">
                    Request Call Back
                  </button>
                </div>
              )}

              {tab === "appointment" && (
                <div>
                  <p className="mb-3 text-sm text-slate-300">Book a 1:1 consultation:</p>
                  <div className="grid grid-cols-2 gap-2">
                    {SLOTS.map((s) => (
                      <button
                        key={s}
                        data-testid={`adviser-slot-${s.replace(/[^a-z0-9]/gi, "-")}`}
                        onClick={() => setBooked(s)}
                        className="rounded-xl border px-3 py-3 text-xs transition-colors"
                        style={{
                          borderColor: booked === s ? "rgba(16,185,129,0.5)" : "rgba(255,255,255,0.08)",
                          background: booked === s ? "rgba(16,185,129,0.12)" : "transparent",
                          color: booked === s ? "#10B981" : "#CBD5E1",
                        }}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                  {booked && (
                    <div className="mt-4 flex items-center gap-2 rounded-xl border border-emerald-400/30 bg-emerald-400/10 p-3 text-sm text-emerald-300">
                      <CheckCircle2 size={18} /> Consultation confirmed for {booked}.
                    </div>
                  )}
                </div>
              )}
            </div>

            {(tab === "message" || tab === "ask") && (
              <div className="flex items-center gap-2 border-t border-white/10 p-3">
                <input
                  data-testid="adviser-message-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder="Type your message…"
                  className="flex-1 rounded-full border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white outline-none placeholder:text-slate-400 focus:border-amber-300/40"
                />
                <button
                  data-testid="adviser-send"
                  onClick={send}
                  className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-amber-300 text-[#07090E]"
                >
                  <Send size={16} />
                </button>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
